import type { ApiAssignedTask, ApiTimesheetEntry } from '../models/pm-timesheet.models';
import {
  parseBillableFromTaskPayload,
  resolveBillableFromTaskPayload,
} from '../../timesheet-final/services/timesheet.service';

/** Whether a PM task row should show the billable dollar pill. */
export function pmShowTaskBillablePill(taskName: string | null | undefined, taskId?: number | null): boolean {
  const name = (taskName ?? '').trim();
  if (name && name !== '—') return true;
  const tid = taskId != null ? Number(taskId) : NaN;
  return Number.isFinite(tid) && tid > 0;
}

export function pmBillablePillAriaLabel(billable: boolean): string {
  return billable ? 'Billable task' : 'Non-billable task';
}

export function pmBillablePillTitle(billable: boolean): string {
  return billable ? 'Billable' : 'Non-billable';
}

/** `projectId|taskId` → billable from GET user-project-task-board assigned tasks. */
export function pmBuildBillableLookupFromAssignedTasks(
  tasks: readonly ApiAssignedTask[],
): ReadonlyMap<string, boolean> {
  const out = new Map<string, boolean>();
  for (const task of tasks) {
    const taskId = Number(task.taskId);
    if (!Number.isFinite(taskId) || taskId <= 0) continue;
    const projectId = String(task.projectId ?? '').trim();
    if (!projectId) continue;
    out.set(`${projectId}|${taskId}`, task.isBillable);
  }
  return out;
}

/** Prefer billing on the entry line; else user-project-task-board lookup; else billable default. */
export function pmResolveEntryBillable(
  entry: Pick<ApiTimesheetEntry, 'projectId' | 'taskId' | 'isBillable'>,
  taskBoardBillable: ReadonlyMap<string, boolean>,
): boolean {
  const fromEntry = parseBillableFromTaskPayload(entry);
  if (fromEntry != null) return fromEntry;

  const taskId = entry.taskId;
  if (taskId != null && taskId > 0) {
    const projectId = String(entry.projectId ?? '').trim();
    if (projectId) {
      const fromBoard = taskBoardBillable.get(`${projectId}|${taskId}`);
      if (fromBoard != null) return fromBoard;
    }
  }

  return entry.isBillable ?? true;
}

export function pmEnrichEntriesWithTaskBoardBillable<T extends ApiTimesheetEntry>(
  entries: readonly T[],
  assignedTasks: readonly ApiAssignedTask[],
): T[] {
  const lookup = pmBuildBillableLookupFromAssignedTasks(assignedTasks);
  return entries.map((entry) => ({
    ...entry,
    isBillable: pmResolveEntryBillable(entry, lookup),
  }));
}

export { resolveBillableFromTaskPayload };
